import { useCallback, useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// Shared "install this PWA" logic for the banner, interstitial, and button.
//  - Android/desktop Chrome/Edge: native beforeinstallprompt dialog.
//  - iOS Safari: no programmatic install → the Share → Add to Home Screen sheet.
//  - In-app browsers (Instagram/Telegram/…): install is impossible → tell them to reopen
//    in a real browser and offer a copy-link button.
//  - Already installed (standalone) or not installable: `visible` is false.
export function useInstall() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);
  const [showIos, setShowIos] = useState(false);
  const [copied, setCopied] = useState(false);

  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isIOS = (/iphone|ipad|ipod/i.test(ua)
    || (typeof navigator !== 'undefined' && navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1))
    && !(typeof window !== 'undefined' && (window as unknown as { MSStream?: unknown }).MSStream);
  const isInApp = /Instagram|FBAN|FBAV|FB_IAB|Telegram|Line\/|MicroMessenger|TikTok|musical_ly|OKApp|VKAndroidApp|VKClient/i.test(ua)
    || (isIOS && !/Safari/i.test(ua) && !(navigator as unknown as { standalone?: boolean }).standalone);
  const canWebShare = typeof navigator !== 'undefined' && typeof (navigator as unknown as { share?: unknown }).share === 'function';

  useEffect(() => {
    const standalone = window.matchMedia?.('(display-mode: standalone)').matches
      || (navigator as unknown as { standalone?: boolean }).standalone;
    if (standalone) { setInstalled(true); return; }
    const onPrompt = (e: Event) => { e.preventDefault(); setDeferred(e as BeforeInstallPromptEvent); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  // Something to offer? (don't show install UI to desktop browsers that can't install)
  const visible = !installed && (!!deferred || isIOS || isInApp);

  const install = useCallback(async () => {
    if (isInApp) {
      try { await navigator.clipboard.writeText(window.location.origin); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* ignore */ }
      return;
    }
    if (deferred) {
      await deferred.prompt();
      try { await deferred.userChoice; } catch { /* ignore */ }
      setDeferred(null);
    } else {
      setShowIos(true);
    }
  }, [isInApp, deferred]);

  const openShareSheet = useCallback(async () => {
    try { await (navigator as unknown as { share: (d: object) => Promise<void> }).share({ title: 'AdaMar', url: window.location.origin }); } catch { /* cancelled / unsupported */ }
  }, []);

  return { installed, isIOS, isInApp, canWebShare, visible, copied, showIos, setShowIos, install, openShareSheet };
}
